import { Capacitor } from '@capacitor/core';

export type NativeContactPermission = 'prompt' | 'prompt-with-rationale' | 'granted' | 'limited' | 'denied';

export type NativeContactPhone = { number?: string | null };
export type NativeContact = {
  name?: { display?: string | null; given?: string | null; family?: string | null };
  phones?: NativeContactPhone[];
};

export type NativeContactsPlugin = {
  checkPermissions: () => Promise<{ contacts?: string }>;
  requestPermissions: () => Promise<{ contacts?: string }>;
  getContacts: (options: { projection: { name: boolean; phones: boolean } }) => Promise<{ contacts?: NativeContact[] }>;
  pickContact: (options: { projection: { name: boolean; phones: boolean } }) => Promise<{ contact?: NativeContact }>;
  createContact: (options: { contact: { name: { given: string; family?: string }; phones: { type: string; label: string; number: string }[]; emails?: { type: string; label: string; address: string }[] } }) => Promise<unknown>;
};

let cachedPlugin: NativeContactsPlugin | null = null;
let inflightRequest: Promise<NativeContactPermission> | null = null;

function createNativeContactsFacade(plugin: NativeContactsPlugin): NativeContactsPlugin {
  return {
    checkPermissions: () => plugin.checkPermissions(),
    requestPermissions: () => plugin.requestPermissions(),
    getContacts: (options) => plugin.getContacts(options),
    pickContact: (options) => plugin.pickContact(options),
    createContact: (options) => plugin.createContact(options),
  };
}

export function mapNativeContactPermission(status: string | undefined): NativeContactPermission {
  if (status === 'granted' || status === 'limited') return status;
  if (status === 'denied') return 'denied';
  if (status === 'prompt-with-rationale') return 'prompt-with-rationale';
  return 'prompt';
}

export function canReadNativeContacts(status: NativeContactPermission): boolean {
  return status === 'granted' || status === 'limited';
}

export async function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((resolve) => {
        timer = setTimeout(() => resolve(fallback), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function getNativeContactsPlugin(): Promise<NativeContactsPlugin | null> {
  if (!Capacitor.isNativePlatform()) return null;
  if (cachedPlugin) return cachedPlugin;
  try {
    const mod = await import('@capacitor-community/contacts');
    // Capacitor plugin proxies expose any property as a native method. Returning
    // that proxy directly from an async function makes JavaScript probe `.then`
    // for promise assimilation, which incorrectly invokes native `Contacts.then()`.
    cachedPlugin = createNativeContactsFacade(mod.Contacts as NativeContactsPlugin);
    console.info('[NativeContacts] plugin loaded');
    return { ...cachedPlugin };
  } catch (err) {
    console.warn('[NativeContacts] failed to load plugin', err);
    return null;
  }
}

export async function checkNativeContactsPermission(): Promise<NativeContactPermission> {
  const plugin = await getNativeContactsPlugin();
  if (!plugin) return 'denied';
  try {
    const result = await withTimeout(
      plugin.checkPermissions(),
      5000,
      { contacts: 'prompt' }
    );
    const status = mapNativeContactPermission(result.contacts);
    console.info('[NativeContacts] checkPermissions', { raw: result.contacts, status });
    return status;
  } catch (err) {
    console.warn('[NativeContacts] checkPermissions failed', err);
    return 'denied';
  }
}

export class NativeContactsRequestError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'NativeContactsRequestError';
  }
}

/** Hard ceiling so the UI can never hang on a stuck native bridge.
 * Real iOS dialogs resolve as soon as the user taps Allow/Don't Allow - they
 * never need a full minute. If we hit this we know the bridge is wedged
 * (missing NSContactsUsageDescription, plugin not registered, simulator
 * preflight crash, etc.) and we surface that to the caller instead of
 * leaving the user stuck on a "Requesting…" spinner. */
const REQUEST_PERMISSION_HARD_TIMEOUT_MS = 60_000;

/**
 * Request iOS/Android contacts permission. We deduplicate concurrent calls
 * with an in-flight guard so a double-tap cannot fire two prompts, and we
 * apply a hard ceiling so the JS promise can never hang forever if the
 * native bridge is wedged.
 */
export async function requestNativeContactsPermission(): Promise<NativeContactPermission> {
  const plugin = await getNativeContactsPlugin();
  if (!plugin) return 'denied';

  if (inflightRequest) {
    console.info('[NativeContacts] reusing in-flight permission request');
    return inflightRequest;
  }

  inflightRequest = (async () => {
    const before = await checkNativeContactsPermission();
    if (canReadNativeContacts(before) || before === 'denied') return before;

    console.info('[NativeContacts] calling iOS CNContactStore.requestAccess via plugin', { before });

    let raw: { contacts?: string } | null = null;
    let nativeError: unknown = null;
    const startedAt = Date.now();
    try {
      raw = await Promise.race([
        plugin.requestPermissions().then((r) => {
          console.info('[NativeContacts] plugin.requestPermissions resolved', { ms: Date.now() - startedAt, raw: r });
          return r;
        }),
        new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new NativeContactsRequestError('iOS Contacts permission prompt did not return in time. The native bridge is wedged - likely missing NSContactsUsageDescription or stale install.')),
            REQUEST_PERMISSION_HARD_TIMEOUT_MS,
          ),
        ),
      ]);
    } catch (err) {
      nativeError = err;
      console.error('[NativeContacts] requestPermissions failed', err);
    }

    if (raw) {
      const status = mapNativeContactPermission(raw.contacts);
      console.info('[NativeContacts] requestPermissions resolved', { raw: raw.contacts, status });
      if (canReadNativeContacts(status) || status === 'denied') return status;

      // Some iOS builds resolve checkPermissions before status is committed.
      const final = await checkNativeContactsPermission();
      console.info('[NativeContacts] final permission status', { final });
      return final;
    }

    // Native call threw or timed out. Re-check status - iOS may have
    // committed the decision even if the bridge promise never resolved.
    const final = await checkNativeContactsPermission();
    if (canReadNativeContacts(final) || final === 'denied') return final;

    // Still no determination - propagate the real error so the UI can
    // surface it instead of silently leaving the user on a spinner.
    throw new NativeContactsRequestError(
      nativeError instanceof Error ? nativeError.message : 'Native Contacts request failed without a result.',
      nativeError,
    );
  })();

  try {
    return await inflightRequest;
  } finally {
    inflightRequest = null;
  }
}

export function notifyNativeContactsPermissionChanged(permission: NativeContactPermission) {
  window.dispatchEvent(new CustomEvent('elefa:native-contacts-permission', { detail: { permission } }));
}
